<template>
  <VCard
    :loading="isLoading"
    class="p-0 shadow-none border-radius-8 px-4 py-4 position-relative"
  >
    <template #progress>
      <SSpinner :dialog="true" />
    </template>
    <VCardTitle class="p-0 d-flex justify-content-between align-center position-relative">
      <div>
        <span class="heading-6">
          {{ $t('settings.changePassword') }}
        </span>
      </div>
      <VBtn
        icon
        class="grey-lighten-4 box-shadow-elevation-6"
        data-test="close-change-password-dialog"
        @click="$emit('closeDialog')"
      >
        <VIcon color="textPrimary">mdi-close</VIcon>
      </VBtn>
    </VCardTitle>
    <ValidationObserver
      ref="changePasswordForm"
      v-slot="{ invalid }"
      name="changePasswordForm"
      tag="div"
    >
      <VCardText class="p-0 pt-6">
        <div class="card-text-header">
          <span class="body-2 textPrimary--text">
            {{ $t('settings.setUpYourNewPassword') }}
          </span>
        </div>
        <div class="card-text-body">
          <ValidationProvider
            v-slot="{ errors, ariaInput }"
            ref="newPassword"
            name="newPassword"
            :rules="{ required: true, min: 6, max: 40, withoutSpaces: /^\S*$/ }"
            :allowFalse="false"
            mode="aggressive"
          >
            <div class="pt-6">
              <VTextField
                v-bind="ariaInput"
                v-model="newPassword"
                :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showNewPassword ? 'text' : 'password'"
                data-test="new-password-input"
                :error="!!errors.length"
                :error-messages="validationErrorResolve(errors[0])"
                :hide-details="!errors.length"
                :label="$t('settings.newPassword')"
                class="body-1 pt-0"
                @click:append="showNewPassword = !showNewPassword"
              />
            </div>
          </ValidationProvider>
          <div class="pt-6">
            <ValidationProvider
              v-slot="{ errors, ariaInput }"
              ref="confirmPassword"
              name="confirmPassword"
              :rules="{ required: true, equalsPassword: newPassword }"
              :allowFalse="false"
              mode="aggressive"
            >
              <VTextField
                v-bind="ariaInput"
                v-model="confirmPassword"
                :append-icon="showConfirmNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showConfirmNewPassword ? 'text' : 'password'"
                data-test="confirm-password-input"
                :label="$t('settings.confirmPassword')"
                :error="!!errors.length"
                :hide-details="!errors.length"
                :error-messages="validationErrorResolve(errors[0])"
                class="body-1 pt-0"
                @click:append="showConfirmNewPassword = !showConfirmNewPassword"
              />
            </ValidationProvider>
          </div>
        </div>
      </VCardText>
      <VCardActions class="px-0 pb-0 pt-4">
        <div class="d-flex align-center justify-content-between w-100">
          <div class="w-50 pr-2">
            <VBtn
              v-blur
              class="w-100 body-2 border-radius-8"
              outlined
              large
              color="textTertiary"
              data-test="cancel-change-password-button"
              @click="$emit('closeDialog')"
            >
              {{ $t('settings.cancel') }}
            </VBtn>
          </div>
          <div class="w-50 pl-2">
            <VBtn
              v-blur
              class="w-100 body-2 border-radius-8"
              dark
              large
              data-test="save-new-password-button"
              :class="{'disabled': invalid }"
              color="brandingPrimary"
              @click="changePassword"
            >
              {{ $t('general.save') }}
            </VBtn>
          </div>
        </div>
      </VCardActions>
    </ValidationObserver>
  </VCard>
</template>

<script lang="ts">
export default {
  name: 'ChangePasswordDialog',
  // Kept in Options data so template refs (newPassword/confirmPassword) stay on $refs.
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
    };
  },
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import SUserService from '@slg/web-customer-shared/src/services/UserService';
import SSpinner from '@slg/web-customer-shared/src/components/common/SSpinner';
import { useGlobalErrorDialog, useValidationError, useVm } from '../../../composables';

const emit = defineEmits(['onChangePasswordSuccess']);

const { validationErrorResolve } = useValidationError();
const { setGlobalError } = useGlobalErrorDialog();
const vm = useVm() as { newPassword: string };

const isLoading = ref(false);
const showNewPassword = ref(false);
const showConfirmNewPassword = ref(false);

async function changePassword() {
  isLoading.value = true;
  try {
    await SUserService.updateProfile({ password: vm.newPassword });
    emit('onChangePasswordSuccess');
  } catch (e) {
    if ((e as any).status !== 401) {
      await setGlobalError(e);
    }
  }
  isLoading.value = false;
}
</script>

<style scoped lang="scss">
</style>
